/* eslint-disable complexity */
import {HCThemeType, Typography, MOMENTJS_DATE_FORMAT, SingleDatePicker} from '@hconnect/uikit'
import {Grid, makeStyles} from '@material-ui/core'
import {isAfter} from 'date-fns'
import moment from 'moment-timezone'
import React, {useEffect, useRef, useState} from 'react'
import {Controller, useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {useSelector} from 'react-redux'

import {useGetBulkCemSlots} from '../../../../common/react-query/hooks/queries/useGetBulkCemSlots'
import {TimeSlotGrid} from '../../../../OrderIntake/BulkCement/containers/slotManagement/components/TImeSlotGrid'
import {TimePicker} from '../../../../OrderIntake/components/TimePicker'
import {
  DATA_BASE_TIME_FORMAT,
  evaluateDeliveryTime
} from '../../../../OrderIntake/components/TimeScroller/TimeScroller.utils'
import {useFeaturesState} from '../../../../Organisms/Features'
import {
  getTodaysCutOffTime,
  getUnavailableDaysFromTimeSlots,
  isCurrentTimeAfterCutOff,
  mergeDateRanges
} from '../../../../Organisms/OrderIntake/utils'
import {useHasRoleByCustomerId} from '../../../../Roles'
import {AppState} from '../../../../Root.store'
import {createTimeInterval, getTzAwareNow, isDateToday} from '../../../../util/time'
import {
  BUSINESS_HOURS_EARLIEST_DEFAULT,
  BUSINESS_HOURS_LATEST_DEFAULT,
  BUSINESS_HOURS_MIN_FIX_INTERVAL_TIME,
  BUSINESS_HOURS_MIN_INTERVAL_DEFAULT
} from '../../../declarations/constants'
import {ShippingType} from '../../../declarations/OrderIntake.enums'
import {
  ActiveMaterialDeliveryItem,
  DeliveryTime,
  OrderIntakeMaterialOptionPayload,
  OrderIntakeOption,
  OrderRequest,
  SlotConfiguration,
  SlotSiloConfig,
  TimeSelectorTrackingEventType
} from '../../../declarations/types'
import {useOrderIntakeData} from '../../../hooks'
import {getCustomMessageForDeliverDate} from '../../utils'
import {getDateExceptions} from '../DateFilter'
import {SlotFilter, Slot} from '../slotManagement/types'

import testIds from './dataTestIds'
import {MaterialOrderForm} from './types'
import {validateDeliveryDates} from './utils'
import {useIsSplitLoadEnabled} from '../SplitLoad/hook/useIsSplitLoadEnabled'
import {RETURN_NO_ORDER_INTAKE_WITH_MESSAGE, RETURN_ORDER_INTAKE_WITH_MESSAGE} from '../slotManagement/hooks/returnCode'
import {trackEvent} from '../../../../common/analytics'

const MINUTE_STEP = 30


const useStyles = makeStyles((theme: HCThemeType) => ({
  contentItem: {
    paddingBottom: theme.spacing(3)
  }
}))

type Props = {
  selectedSite: OrderIntakeOption
  slotConfigurations?: SlotConfiguration[]
  order: OrderRequest
  defaultMaterialOption: OrderIntakeMaterialOptionPayload
  initiallyOpenedItem?: ActiveMaterialDeliveryItem
  shippingType: ShippingType
  onTimeSelectorTrack?: (eventType: TimeSelectorTrackingEventType) => void
  activeSelectedDeliveryDate?: string
  setActiveSelectedDeliveryDate?: (date: string) => void
  setSelectedDeliveryTimeSlot?: (slot: DeliveryTime) => void
  selectedDeliveryTimeSlot?: DeliveryTime
  setSelectedSlotSiloConfig: (slotSiloConfig: SlotSiloConfig) => void
  setSelectedSlot: (slot: Slot) => void
  isError: boolean
  externalReference: string
  slots: Slot[]
  setSlots?: (slots: Slot[]) => void
}

export const DateTimeTab: React.FC<Props> = ({
  selectedSite,
  slotConfigurations,
  order,
  defaultMaterialOption,
  shippingType,
  onTimeSelectorTrack,
  activeSelectedDeliveryDate,
  setActiveSelectedDeliveryDate,
  setSelectedDeliveryTimeSlot,
  selectedDeliveryTimeSlot,
  setSelectedSlotSiloConfig,
  setSelectedSlot,
  isError,
  externalReference,
  slots,
  setSlots
}) => {
  const firstUpdate = useRef(true)
  const {t} = useTranslation()
  const c = useStyles()
  const methods = useFormContext<MaterialOrderForm>()
  const {getFeature} = useFeaturesState()
  const {customerId} = useOrderIntakeData()
  const {data: isOrderPlacerFixtime} = useHasRoleByCustomerId(customerId, 'ORDER_PLACER_FIXTIME')
  const hasFixedTime = isOrderPlacerFixtime && shippingType === ShippingType.DELIVER
  const businessHours = defaultMaterialOption.businessHours
  const tz = businessHours?.timeZone ?? ''

  const deliveryDateRange = mergeDateRanges(
    (selectedSite.materials[order.payload.materialEnteredNumber] ?? []).map(
      (material) => material.dateRange
    )
  )

  const businessDays = deliveryDateRange?.businessDays ? deliveryDateRange?.businessDays : []
  const todaysCutOffTime = getTodaysCutOffTime(businessDays)[0].cutOffTime
  const cutOffTimeStamp = todaysCutOffTime?.timestamp

  const isAfterCutoffTime = isCurrentTimeAfterCutOff(cutOffTimeStamp, tz)
  const customMessage = getCustomMessageForDeliverDate(deliveryDateRange, t)
  const earliestPossible = businessHours?.earliestPossible ?? BUSINESS_HOURS_EARLIEST_DEFAULT
  const latestPossible = businessHours?.latestPossible ?? BUSINESS_HOURS_LATEST_DEFAULT
  const minInterval = businessHours?.minInterval ?? BUSINESS_HOURS_MIN_INTERVAL_DEFAULT
  const minFixIntervalTime =
    businessHours?.minFixIntervalTime !== '00:00:00' || businessHours?.minFixIntervalTime
      ? businessHours?.minFixIntervalTime
      : BUSINESS_HOURS_MIN_FIX_INTERVAL_TIME
  const timeChangeInterval = !Number.isNaN(Number(businessHours.timeChangeInterval))
    ? Number(businessHours.timeChangeInterval)
    : MINUTE_STEP

  const deliveryDate = methods.watch('dateTime.deliveryDate')

  const selectedMaterialOption = methods.watch('details.selectedMaterialOption')
  const selectedSplitLoadOptions = methods.watch('details.selectedSplitLoadMaterialsOptions')
  const selectedMaterialNumber = selectedMaterialOption?.material.materialNumber
  const selectedPlantNumber = selectedMaterialOption?.plant.plantNumber
  const selectedSplitLoadMaterialNumber = selectedSplitLoadOptions?.materials?.[0]?.materialNumber
  const selectedSplitLoadPlantNumber = selectedSplitLoadOptions?.plant.plantNumber

  const isSplitLoadEnabled = useIsSplitLoadEnabled()

  const isSlotManagementEnabled = getFeature(
    shippingType === ShippingType.DELIVER ? 'OrderIntakeSlotsDeliver' : 'OrderIntakeSlotsCollect'
  )
  const isCollectSlotManagementEnabled =
    shippingType === ShippingType.COLLECT &&
    getFeature('orderIntakeSlotManagedPlantLaneAvailability')

  const [selectedDate, setSelectedDate] = useState(
    activeSelectedDeliveryDate ? moment(activeSelectedDeliveryDate) : null
  )

  useEffect(() => {
    // we need to run this after first render when date changes
    if (firstUpdate.current) {
      firstUpdate.current = false
      return
    }
    const materialOptions = selectedSite.materials[order.payload.materialEnteredNumber]

    const configurableSlot = isSlotManagementEnabled
      ? materialOptions[0].plant.configurableSlots?.filter((s) => s.date === deliveryDate)
      : []

    const evaluateArgs = [
      minInterval,
      DATA_BASE_TIME_FORMAT,
      deliveryDate,
      materialOptions[0].defaultDeliveryWindow,
      materialOptions[0].businessHours,
      materialOptions[0].businessHours.timeZone,
      !getFeature('OrderIntakeCollectTimePicker')
    ] as const

    if (isSlotManagementEnabled) {
      const deliveryTime = evaluateDeliveryTime(
        ...evaluateArgs,
        slotConfigurations,
        configurableSlot
      )

      methods.setValue(
        'dateTime.deliveryTime',
        {
          earliest: deliveryTime.earliest,
          latest: deliveryTime.latest
        },
        {shouldDirty: true}
      )
    }

    methods.clearErrors('dateTime')

    setSelectedDate(activeSelectedDeliveryDate ? moment(activeSelectedDeliveryDate) : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deliveryDate,
    order.payload.materialEnteredNumber,
    selectedSite.materials,
    shippingType,
    activeSelectedDeliveryDate
  ])

  const timeSelectorTrack = (eventType: TimeSelectorTrackingEventType) => {
    onTimeSelectorTrack && onTimeSelectorTrack(eventType)
  }

  const onTimePickerError = (isError: boolean) => {
    isError
      ? methods.setError('dateTime', {type: 'custom', message: ''})
      : methods.clearErrors('dateTime')
  }

  const materialOptions = selectedSite.materials[order.payload.materialEnteredNumber]
  const validMaterialOptions = materialOptions.filter((m) => !m.invalid)

  const showDatePicker =
    getFeature('OrderIntakeCollectTimePicker') || shippingType === ShippingType.DELIVER
  const fullDays = isSlotManagementEnabled
    ? getUnavailableDaysFromTimeSlots(
        businessHours,
        slotConfigurations,
        materialOptions[0].plant.configurableSlots
      ).map((s) => moment(s))
    : []

  const userCountry = useSelector((state: AppState) => state.userProfile.userProfile?.country || '')

  const plantId = isSplitLoadEnabled ? selectedSplitLoadPlantNumber : selectedPlantNumber
  const materialNumber = isSplitLoadEnabled
    ? selectedSplitLoadMaterialNumber
    : selectedMaterialNumber

  const slotFilter: SlotFilter = {
    country: userCountry,
    plantId: plantId ?? '',
    materialNumber: materialNumber ?? '',
    startDate: deliveryDate ? moment(deliveryDate).format(MOMENTJS_DATE_FORMAT) : '',
    endDate: deliveryDate ? moment(deliveryDate).format(MOMENTJS_DATE_FORMAT) : ''
  }

  const {
    data: slotData,
    isLoading: isSlotLoading,
    isSuccess,
    refetch: fetchSlots
  } = useGetBulkCemSlots(slotFilter)

  useEffect(() => {
    if (deliveryDate && isCollectSlotManagementEnabled) {
      void fetchSlots()
    }
    return () => {
      if (!deliveryDate) {
        setSlots?.([])
      }
    }
  }, [fetchSlots, deliveryDate])

  useEffect(() => {
    if (!slotData || isSlotLoading) return
    setSlots?.(slotData)

    if (!isCollectSlotManagementEnabled) return

    trackEvent('hubBulkCementOrderIntakeTimeSlotFetched', {
      plantId: plantId ?? '',
      materialNumber: materialNumber ?? '',
      date: deliveryDate,
      country: userCountry,
      returnCode: slotData[0]?.serviceResponseCode ?? '',
      isSuccess,
      isError
    })  
  }, [isSlotLoading, slotData])

  const slotServiceResponseCode = slots[0]?.serviceResponseCode ?? ''
  const shouldShowSlotWindow =
    isCollectSlotManagementEnabled &&
    (isSlotLoading || slotServiceResponseCode === 'RC0')
  const isSlotBlocked = isCollectSlotManagementEnabled && RETURN_NO_ORDER_INTAKE_WITH_MESSAGE.includes(slotServiceResponseCode)
  const hasSlotWarning = isCollectSlotManagementEnabled && RETURN_ORDER_INTAKE_WITH_MESSAGE.includes(slotServiceResponseCode)

  return (
    <Grid>
      <div className={c.contentItem}>
        <Typography variant="caption" color="secondary">
          {shippingType === ShippingType.DELIVER
            ? t('orderIntake.materialOrder.shipTo')
            : t('orderIntake.materialOrder.location')}
        </Typography>
        <Typography variant="h4" color="textPrimary" data-test-id={testIds.dateTime.shipTo}>
          {defaultMaterialOption.shippingAddress.siteName}
          {tz && ` (${moment.tz(tz ?? '').zoneAbbr()})`}
        </Typography>
      </div>
      <Grid className={c.contentItem}>
        <Controller
          control={methods.control}
          name="dateTime.deliveryDate"
          rules={{
            validate: (value) => {
              return Boolean(value)
            }
          }}
          render={({field}) => {
            return (
              <SingleDatePicker
                onLight
                noQuickSelection
                data-test-id={testIds.dateTime.datePicker}
                fullWidth
                showTooltip={order.isDateChange}
                tooltipLabel={t('orderIntake.dateChanged')}
                customMessage={customMessage}
                date={selectedDate}
                handleDateChange={({startDate}) => {
                  const newDate = moment(startDate).format(MOMENTJS_DATE_FORMAT)
                  setActiveSelectedDeliveryDate && setActiveSelectedDeliveryDate(newDate)
                  setSelectedDate(moment(newDate))
                  field.onChange(newDate)
                }}
                availableDateRange={{
                  from: isAfterCutoffTime
                    ? moment(deliveryDateRange.fromAfterCutoff)
                    : moment(deliveryDateRange.from),
                  to: isAfterCutoffTime
                    ? moment(deliveryDateRange.toAfterCutoff)
                    : moment(deliveryDateRange.to)
                }}
                dateExceptions={[
                  ...getDateExceptions(
                    selectedSite.materials[order.payload.materialEnteredNumber][0].businessHours,
                    deliveryDateRange
                  ),
                  ...fullDays
                ]}
              />
            )
          }}
        />
      </Grid>
      {showDatePicker && deliveryDate && (
        <Grid className={c.contentItem} data-test-id={testIds.dateTime.timePicker}>
          {shouldShowSlotWindow ? (
            <Controller
              control={methods.control}
              name={'dateTime.deliveryTime'}
              rules={{
                validate: (value) => {
                  return Boolean(value.earliest || selectedDeliveryTimeSlot?.earliest)
                }
              }}
              render={({field}) => (
                <TimeSlotGrid
                  setSelectedDeliveryTimeSlot={setSelectedDeliveryTimeSlot}
                  setSelectedSlotSiloConfig={setSelectedSlotSiloConfig}
                  selectedDeliveryTimeSlot={selectedDeliveryTimeSlot}
                  setSelectedSlot={setSelectedSlot}
                  slots={slots}
                  name={field.name}
                  value={field.value}
                  selectedDate={selectedDate}
                  onChange={field.onChange}
                  isLoading={isSlotLoading}
                  isSuccess={isSuccess}
                  isError={isError}
                  externalReference={externalReference}
                />
              )}
            />
          ) : (
            <Controller
              control={methods.control}
              name={'dateTime.deliveryTime'}
              rules={{
                validate: (value) => validateDeliveryDates(value, deliveryDate, businessHours) && !isSlotBlocked
              }}
              render={({field}) => {
                return (
                  <TimePicker
                    isSlotManagementEnabled={isSlotManagementEnabled}
                    onError={onTimePickerError}
                    hasFixedTime={hasFixedTime}
                    name={field.name}
                    value={field.value}
                    deliveryDate={deliveryDate}
                    minInterval={minInterval}
                    onChange={field.onChange}
                    timezone={tz}
                    data-test-id={testIds.dateTime.timePicker}
                    configurableSlots={validMaterialOptions[0].plant.configurableSlots?.filter(
                      (s) => s.date === deliveryDate
                    )}
                    businessHours={businessHours}
                    defaultDeliveryWindow={defaultMaterialOption.defaultDeliveryWindow}
                    slotsConfiguration={slotConfigurations}
                    differentTimeZoneWarning={t(
                      shippingType === ShippingType.DELIVER
                        ? 'orderIntake.supplyingDestinationDifferent'
                        : 'orderIntake.supplyingPlantDifferent'
                    )}
                    interval={createTimeInterval(deliveryDate, earliestPossible, latestPossible)}
                    minFixIntervalTime={minFixIntervalTime}
                    timeChangeInterval={timeChangeInterval}
                    filterTimeSlots={(d: Date) =>
                      !(isDateToday(deliveryDate, tz) && isAfter(getTzAwareNow(tz), d))
                    }
                    onTimeSelectorTrack={timeSelectorTrack}
                    cardId={order.payload.materialNumber}
                    slotServiceResponseCode={slotServiceResponseCode}
                    isSlotBlocked={isSlotBlocked}
                    hasSlotWarning={hasSlotWarning}
                  />
                )
              }}
            />
          )}
        </Grid>
      )}
    </Grid>
  )
}
